import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiErrorDetail } from "../lib/api";
import { Car } from "lucide-react";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("both");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await register({ name, email, password, role });
      nav("/dashboard");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <main className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <Car className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
          </div>
          <span className="font-display font-extrabold text-xl">MY VELOCITY</span>
        </Link>
        <div className="glass rounded-3xl p-8 rise">
          <h1 className="font-display text-3xl font-bold mb-1">Create your account</h1>
          <p className="text-slate-400 text-sm mb-6">Start listing or booking in minutes.</p>
          <Button type="button" onClick={google} data-testid="google-register-btn"
            className="w-full h-11 mb-5 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white font-medium">
            Continue with Google
          </Button>
          <div className="relative my-5 text-center">
            <span className="relative z-10 px-3 bg-[#131722] text-xs font-mono uppercase tracking-widest text-slate-500">or</span>
            <div className="absolute inset-x-0 top-1/2 border-t border-slate-800" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" className="bg-slate-900 border-slate-800 mt-1.5 h-11" /></div>
            <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reg-email" className="bg-slate-900 border-slate-800 mt-1.5 h-11" /></div>
            <div><Label>Password</Label><Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reg-password" className="bg-slate-900 border-slate-800 mt-1.5 h-11" /></div>
            <div>
              <Label>I want to…</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {[{v:"renter",l:"Rent"},{v:"owner",l:"List cars"},{v:"both",l:"Both"}].map((o) => (
                  <button type="button" key={o.v} onClick={() => setRole(o.v)} data-testid={`role-${o.v}`}
                    className={`h-11 rounded-lg border text-sm font-medium transition-all ${role === o.v ? "bg-white text-slate-950 border-white" : "bg-slate-900 border-slate-800 text-slate-300 hover:border-slate-600"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            {err && <p className="text-sm text-red-400" data-testid="reg-error">{err}</p>}
            <Button type="submit" disabled={busy} data-testid="reg-submit" className="w-full h-11 bg-white text-slate-950 hover:bg-slate-100 font-semibold">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="mt-5 text-center text-sm text-slate-400">
            Already have an account? <Link to="/login" className="text-cyan-400 hover:text-cyan-300">Sign in</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
